import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_SELLER_PROFILE } from 'api/graphql/mutations/seller/updateProfile';
import { GET_SELLER_PROFILE } from 'api/graphql/queries/getSellerProfileQuery';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider';
import { fileToBase64 } from 'utils/imageUtils';

interface UpdateProfileOptions {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

/**
 * A hook for handling profile updates in a consolidated way
 * Tracks changes and only submits modified fields to the server
 */
export const useProfileUpdater = (options?: UpdateProfileOptions) => {
  const { t } = useTranslation();
  const { seller, setSeller } = useAuth();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');
  
  // Keep track of changes to be submitted
  const [pendingChanges, setPendingChanges] = useState<Record<string, any>>({});
  
  // Get profile query to fetch fresh data
  const { refetch } = useQuery(GET_SELLER_PROFILE, {
    fetchPolicy: 'network-only',
    skip: true, // Don't run on mount
  });
  
  // Update GraphQL mutation
  const [updateProfile, { loading: isSubmitting }] = useMutation(UPDATE_SELLER_PROFILE, {
    onCompleted: (data) => {
      if (data.updateSellerProfile.success) {
        // Show success message
        setToastMessage(t('updateSuccess'));
        setToastVariant('success');
        setShowToast(true);
        
        // Directly update context with updated seller data 
        // from the mutation response if available
        if (data.updateSellerProfile.data && setSeller && seller) {
          const updatedSellerData = data.updateSellerProfile.data;
          const updatedSeller = {
            ...seller,
            ...updatedSellerData
          };
          
          // Update Auth context
          setSeller(updatedSeller);
          
          // Update localStorage
          localStorage.setItem('seller', JSON.stringify(updatedSeller));
        } else {
          // If data isn't directly available in the mutation response,
          // refetch profile to ensure state is up-to-date
          refetchProfileData();
        }
        
        // Call onSuccess callback if provided
        if (options?.onSuccess) {
          options.onSuccess();
        }
      } else {
        // Show error message
        setToastMessage(data.updateSellerProfile.message || t('updateError'));
        setToastVariant('error');
        setShowToast(true);
        
        // Call onError callback if provided
        if (options?.onError) {
          options.onError(new Error(data.updateSellerProfile.message));
        }
      }
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
      setToastMessage(error.message || t('updateError'));
      setToastVariant('error');
      setShowToast(true);
      
      // Call onError callback if provided
      if (options?.onError) {
        options.onError(error);
      }
    }
  });
  
  /**
   * Track a field change without submitting
   * @param fieldName Field name to update
   * @param value New value
   */
  const trackChange = (fieldName: string, value: any) => {
    // If the value hasn't changed, don't track it
    if (seller && seller[fieldName] === value) {
      // Remove from pending changes if it was previously added
      if (pendingChanges[fieldName]) {
        const { [fieldName]: _, ...rest } = pendingChanges;
        setPendingChanges(rest);
      }
      return;
    }
    
    // Add to pending changes
    setPendingChanges(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };
  
  /**
   * Submit all pending changes to the server
   */
  const submitChanges = async () => {
    // If no changes, don't submit
    if (Object.keys(pendingChanges).length === 0) {
      console.log('No changes to submit');
      return;
    }
    
    try {
      // Make a copy to process
      const input: Record<string, any> = { ...pendingChanges };
      
      // Process any file uploads (convert to base64)
      for (const [key, value] of Object.entries(input)) {
        if (value instanceof File) {
          input[key] = await fileToBase64(value);
        }
      }
      
      // Update local state first for optimistic UI update
      if (setSeller && seller) {
        const updatedSeller = {
          ...seller,
          ...pendingChanges
        };
        
        // For file objects, use the file name for display
        for (const [key, value] of Object.entries(pendingChanges)) {
          if (value instanceof File) {
            updatedSeller[key] = value.name;
          }
        }
        
        setSeller(updatedSeller);
        localStorage.setItem('seller', JSON.stringify(updatedSeller));
      }
      
      // Submit the GraphQL mutation with only changed fields
      await updateProfile({
        variables: {
          input
        }
      });
      
      // Clear pending changes after successful submission
      setPendingChanges({});
    } catch (error) {
      console.error('Error processing profile update:', error);
      setToastMessage(t('updateError'));
      setToastVariant('error');
      setShowToast(true);
      
      // Call onError callback if provided
      if (options?.onError) {
        options.onError(error);
      }
    }
  };
  
  /**
   * Track and immediately submit a single field change
   * @param fieldName Field name to update
   * @param value New value
   */
  const updateField = async (fieldName: string, value: any) => {
    try {
      const input: Record<string, any> = { [fieldName]: value };
      
      // Process file uploads
      if (value instanceof File) {
        input[fieldName] = await fileToBase64(value);
      }
      
      // Update local state first for optimistic UI update
      if (setSeller && seller) {
        const updatedSeller = { ...seller };
        
        // Handle file objects specially for display
        if (value instanceof File) {
          updatedSeller[fieldName] = value.name;
        } else {
          updatedSeller[fieldName] = value;
        }
        
        setSeller(updatedSeller);
        localStorage.setItem('seller', JSON.stringify(updatedSeller));
      }
      
      // Submit the GraphQL mutation with only this field
      await updateProfile({
        variables: {
          input
        }
      });
    } catch (error) {
      console.error(`Error updating field ${fieldName}:`, error);
      setToastMessage(t('updateError'));
      setToastVariant('error');
      setShowToast(true);
      
      // Call onError callback if provided
      if (options?.onError) {
        options.onError(error);
      }
    }
  };
  
  /**
   * Function to refetch profile data and update context
   */
  const refetchProfileData = async () => {
    try {
      const result = await refetch();
      
      if (result.data?.getSellerProfile?.success && result.data.getSellerProfile.data) {
        // Get fresh data from server
        const freshData = result.data.getSellerProfile.data;
        
        // Update Auth context if setter is available
        if (setSeller && seller) {
          const updatedSeller = {
            ...seller,
            ...freshData
          };
          
          // Update both Auth context and localStorage
          setSeller(updatedSeller);
          localStorage.setItem('seller', JSON.stringify(updatedSeller));
        }
      }
    } catch (error) {
      console.error('Error refetching profile data:', error);
    }
  };
  
  // Compute whether there are any pending changes
  const hasPendingChanges = Object.keys(pendingChanges).length > 0;
  
  return {
    trackChange,         // Track a change without submitting
    submitChanges,       // Submit all tracked changes
    updateField,         // Update a single field immediately
    pendingChanges,      // Current pending changes
    hasPendingChanges,   // Boolean indicating if there are pending changes
    isSubmitting,        // Loading state
    showToast,           // Toast visibility state
    setShowToast,        // Toast visibility control
    toastMessage,        // Toast message
    toastVariant,        // Toast variant (success/error)
    refetchProfileData   // Manually refetch profile data and update context
  };
};
